import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Stack, StackProps, TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { forwardRef, useEffect, useRef, useState } from 'react';

interface IncrementerButtonProps extends StackProps {
  name?: string;
  initialValue?: number;
  value: number;
  max?: number;
  onMaxReached?: VoidFunction;
  disabledIncrease?: boolean;
  disabledDecrease?: boolean;
  onIncrease: VoidFunction;
  onDecrease: VoidFunction;
  onQtyChange: (qty: number) => void;
  disabled?: boolean;
  /** When true, parses decimals (2 dp) instead of integers only. */
  allowDecimalQuantity?: boolean;
}

const roundToTwoDecimals = (n: number) => Math.round(n * 100) / 100;

const sameQuantity = (
  a: number,
  b: number,
  allowDecimalQuantity: boolean
) =>
  allowDecimalQuantity ? Math.abs(a - b) < 1e-6 : a === b;

const IncrementerButton = forwardRef<HTMLDivElement, IncrementerButtonProps>(
  (
    {
      initialValue,
      value,
      max,
      onIncrease,
      onDecrease,
      onQtyChange,
      onMaxReached,
      disabledIncrease,
      disabledDecrease,
      disabled,
      allowDecimalQuantity = false,
      sx,
      ...other
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState<string>(() =>
      (initialValue ?? value).toString()
    );
    /** Synchronous — `setIsEditing` from +/- can lag one frame and let effects fight the parent. */
    const isTypingInFieldRef = useRef(false);

    useEffect(() => {
      if (isTypingInFieldRef.current) return;
      setInternalValue(value.toString());
    }, [value]);

    const tryCommitParsedString = (raw: string, committedValue: number) => {
      if (raw === '' || raw === '-') return;
      if (allowDecimalQuantity && /\d+\.$/.test(raw.trim())) return;

      const parsedValue = allowDecimalQuantity
        ? parseFloat(raw)
        : parseInt(raw, 10);
      if (isNaN(parsedValue)) return;

      const rounded = allowDecimalQuantity
        ? roundToTwoDecimals(parsedValue)
        : parsedValue;

      if (sameQuantity(rounded, committedValue, allowDecimalQuantity)) return;

      onQtyChange(rounded);
    };

    const sanitizeNumericInput = (raw: string) => {
      let cleaned = raw.replace(/[^0-9.]/g, '');
      if (!allowDecimalQuantity) {
        return cleaned.replace(/\./g, '');
      }
      const firstDot = cleaned.indexOf('.');
      if (firstDot === -1) return cleaned;
      const head = cleaned.slice(0, firstDot + 1);
      const tail = cleaned
        .slice(firstDot + 1)
        .replace(/\./g, '');
      return head + tail;
    };

    return (
      <Stack
        ref={ref}
        flexShrink={0}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          py: 0.57,
          px: 0.75,
          borderRadius: 1,
          border: (theme) =>
            `solid 1px ${alpha(theme.palette.grey[500], 0.32)}`,
          ...sx,
        }}
        {...other}
      >
        <IconButton
          size="small"
          color="inherit"
          onClick={() => {
            isTypingInFieldRef.current = false;
            onDecrease();
          }}
          disabled={disabledDecrease || disabled}
          sx={{ color: 'text.secondary' }}
        >
          <FontAwesomeIcon icon={faMinus} width={16} />
        </IconButton>

        <TextField
          value={internalValue}
          size="small"
          disabled={disabled}
          style={{
            backgroundColor: '#fff',
          }}
          onFocus={() => {
            isTypingInFieldRef.current = true;
          }}
          onChange={(e) => {
            const parsedValue = sanitizeNumericInput(e.target.value);
            let numValue = allowDecimalQuantity
              ? parseFloat(parsedValue)
              : parseInt(parsedValue, 10);

            if (
              parsedValue !== '' &&
              !isNaN(numValue) &&
              max !== undefined &&
              numValue > max
            ) {
              const clamped = allowDecimalQuantity
                ? roundToTwoDecimals(max)
                : max;
              setInternalValue(clamped.toString());
              onMaxReached?.();
              if (!sameQuantity(clamped, value, allowDecimalQuantity)) {
                onQtyChange(clamped);
              }
              return;
            }

            setInternalValue(parsedValue);
            if (isTypingInFieldRef.current) {
              tryCommitParsedString(parsedValue, value);
            }
          }}
          onBlur={(e) => {
            isTypingInFieldRef.current = false;
            const raw = sanitizeNumericInput(
              (e.target as HTMLInputElement).value
            );
            if (raw === '' || raw === '-') {
              setInternalValue(value.toString());
              return;
            }
            const parsedValue = allowDecimalQuantity
              ? parseFloat(raw)
              : parseInt(raw, 10);
            if (isNaN(parsedValue)) {
              setInternalValue(value.toString());
              return;
            }
            const rounded = allowDecimalQuantity
              ? roundToTwoDecimals(parsedValue)
              : parsedValue;
            setInternalValue(rounded.toString());
            if (!sameQuantity(rounded, value, allowDecimalQuantity)) {
              onQtyChange(rounded);
            }
          }}
        />

        <IconButton
          size="small"
          color="inherit"
          onClick={() => {
            isTypingInFieldRef.current = false;
            onIncrease();
          }}
          disabled={disabledIncrease || disabled}
          sx={{ color: 'text.secondary' }}
        >
          <FontAwesomeIcon icon={faPlus} width={16} />
        </IconButton>
      </Stack>
    );
  }
);

export default IncrementerButton;
